const TRUTHY_UPLOAD_VALUES = new Set([1, "1", true, "true"]);

export const KYC_UPLOAD_DOC_TYPES = {
  panCard: "isPanUploaded",
  aadharCardFront: "isAadhaarFrontUploaded",
  aadharCardBack: "isAadhaarBackUploaded",
};

export const isKycDocumentUploaded = (value) => TRUTHY_UPLOAD_VALUES.has(value);

const readUploadFlag = (source = {}, keys = []) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

export const CKYC_DOC_STATUS = {
  IDLE: "",
  APPROVED: "APPROVED",
  VERIFIED: "APPROVED",
  UPLOADED: "UPLOADED",
  FAILED: "FAILED",
  PENDING: "PENDING",
};

const FAILED_UPLOAD = {
  isFailed: true,
  isPending: false,
  approved: false,
  isDocumentVerified: false,
  isUploadedOnly: false,
  docStatus: CKYC_DOC_STATUS.FAILED,
};

export const emptyCkycDocStatus = () => ({
  panCard: CKYC_DOC_STATUS.IDLE,
  aadharCardFront: CKYC_DOC_STATUS.IDLE,
  aadharCardBack: CKYC_DOC_STATUS.IDLE,
});

export const normalizeCkycDocStatus = (value) => {
  const status = String(value || "").toUpperCase();
  if (status === "REJECTED" || status === "FAILED") {
    return CKYC_DOC_STATUS.FAILED;
  }
  if (status === "PENDING" || status === "PROCESSING" || status === "IN_REVIEW") {
    return CKYC_DOC_STATUS.PENDING;
  }
  if (
    status === "APPROVED" ||
    status === "VERIFIED" ||
    status === "SUCCESS"
  ) {
    return CKYC_DOC_STATUS.APPROVED;
  }
  if (status === "UPLOADED") {
    return CKYC_DOC_STATUS.UPLOADED;
  }
  return CKYC_DOC_STATUS.IDLE;
};

export const isCkycDocAccepted = (status) =>
  status === CKYC_DOC_STATUS.APPROVED;

export const persistCkycDocStatus = (customerId, status) => {
  if (!customerId || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(
      `ckycDocStatus:${customerId}`,
      JSON.stringify(status || emptyCkycDocStatus()),
    );
  } catch (e) {
    // ignore quota / private mode
  }
};

export const readPersistedCkycDocStatus = (customerId) => {
  if (!customerId || typeof sessionStorage === "undefined") {
    return emptyCkycDocStatus();
  }
  try {
    const raw = sessionStorage.getItem(`ckycDocStatus:${customerId}`);
    if (!raw) {
      return emptyCkycDocStatus();
    }
    return { ...emptyCkycDocStatus(), ...JSON.parse(raw) };
  } catch (e) {
    return emptyCkycDocStatus();
  }
};

const keepLocalCkycStatus = (status) =>
  status === CKYC_DOC_STATUS.PENDING ||
  status === CKYC_DOC_STATUS.FAILED ||
  status === CKYC_DOC_STATUS.APPROVED
    ? status
    : CKYC_DOC_STATUS.IDLE;

const statusFromDetailsField = (verification, _uploadedFlag, prevStatus) => {
  if (verification === undefined || verification === null) {
    return keepLocalCkycStatus(prevStatus);
  }
  const fromVerification = normalizeCkycDocStatus(verification);
  if (
    fromVerification === CKYC_DOC_STATUS.PENDING ||
    fromVerification === CKYC_DOC_STATUS.FAILED ||
    fromVerification === CKYC_DOC_STATUS.APPROVED
  ) {
    return fromVerification;
  }
  // Explicit "" from details: never keep stale APPROVED
  return keepLocalCkycStatus(prevStatus);
};

const firstPresentValue = (source = {}, keys = []) => {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }
    const value = source[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

export const readCkycVerificationFromDetails = (res = {}) => {
  const panRaw = firstPresentValue(res, [
    "panVerification",
    "panCardVerification",
  ]);
  const frontRaw = firstPresentValue(res, [
    "aadharFrontVerification",
    "aadhaarFrontVerification",
  ]);
  const backRaw = firstPresentValue(res, [
    "aadharBackVerification",
    "aadhaarBackVerification",
  ]);

  return {
    ...(panRaw !== undefined
      ? { panVerification: normalizeCkycDocStatus(panRaw) }
      : {}),
    ...(frontRaw !== undefined
      ? { aadharFrontVerification: normalizeCkycDocStatus(frontRaw) }
      : {}),
    ...(backRaw !== undefined
      ? { aadharBackVerification: normalizeCkycDocStatus(backRaw) }
      : {}),
  };
};

export const mergeCkycDocStatusWithUploadFlags = (status = {}, flags = {}) => ({
  panCard: statusFromDetailsField(
    flags.panVerification,
    flags.isPanUploaded,
    status.panCard,
  ),
  aadharCardFront: statusFromDetailsField(
    flags.aadharFrontVerification ?? flags.aadhaarFrontVerification,
    flags.isAadhaarFrontUploaded,
    status.aadharCardFront,
  ),
  aadharCardBack: statusFromDetailsField(
    flags.aadharBackVerification ?? flags.aadhaarBackVerification,
    flags.isAadhaarBackUploaded,
    status.aadharCardBack,
  ),
});

export const ckycStatusFlagsFromProps = (props = {}) => ({
  isPanUploaded: props.isPanUploaded,
  isAadhaarFrontUploaded: props.isAadhaarFrontUploaded,
  isAadhaarBackUploaded: props.isAadhaarBackUploaded,
  panVerification: props.panVerification,
  aadharFrontVerification: props.aadharFrontVerification,
  aadharBackVerification: props.aadharBackVerification,
});

export const didCkycStatusFlagsChange = (prev = {}, next = {}) => {
  const from = ckycStatusFlagsFromProps(prev);
  const to = ckycStatusFlagsFromProps(next);
  return (
    from.isPanUploaded !== to.isPanUploaded ||
    from.isAadhaarFrontUploaded !== to.isAadhaarFrontUploaded ||
    from.isAadhaarBackUploaded !== to.isAadhaarBackUploaded ||
    from.panVerification !== to.panVerification ||
    from.aadharFrontVerification !== to.aadharFrontVerification ||
    from.aadharBackVerification !== to.aadharBackVerification
  );
};

export const didCkycUploadFlagsChange = (prev = {}, next = {}) => {
  const from = ckycStatusFlagsFromProps(prev);
  const to = ckycStatusFlagsFromProps(next);
  return (
    from.isPanUploaded !== to.isPanUploaded ||
    from.isAadhaarFrontUploaded !== to.isAadhaarFrontUploaded ||
    from.isAadhaarBackUploaded !== to.isAadhaarBackUploaded
  );
};

export const didCkycVerificationChange = (prev = {}, next = {}) => {
  const from = ckycStatusFlagsFromProps(prev);
  const to = ckycStatusFlagsFromProps(next);
  return (
    from.panVerification !== to.panVerification ||
    from.aadharFrontVerification !== to.aadharFrontVerification ||
    from.aadharBackVerification !== to.aadharBackVerification
  );
};

export const nextCkycVerificationFromPayload = (state = {}, payload = {}) => ({
  panVerification: payload.panVerification ?? state.panVerification ?? "",
  aadharFrontVerification:
    payload.aadharFrontVerification ?? state.aadharFrontVerification ?? "",
  aadharBackVerification:
    payload.aadharBackVerification ?? state.aadharBackVerification ?? "",
});

const pendingUpload = (message) => ({
  isFailed: false,
  isPending: true,
  approved: false,
  isDocumentVerified: false,
  isUploadedOnly: false,
  docStatus: CKYC_DOC_STATUS.PENDING,
  message: message || "Your document is under review from our team",
});

const approvedUpload = (message) => ({
  isFailed: false,
  isPending: false,
  approved: true,
  isDocumentVerified: true,
  isUploadedOnly: false,
  docStatus: CKYC_DOC_STATUS.APPROVED,
  message: message || "Document verified successfully",
});

/**
 * Nested `status` is the source of truth.
 * PENDING (verified true or false) = team review, no re-upload.
 * FAILED / REJECTED = allow re-upload.
 * APPROVED = verified.
 */
const getKycUploadResultBody = (res = {}) => {
  if (res?.result && typeof res.result === "object" && !Array.isArray(res.result)) {
    return res.result;
  }
  if (res?.panCard || res?.aadharCardFront || res?.aadharCardBack) {
    return res;
  }
  return {};
};

export const readNestedCkycDocStatusFromUpload = (res = {}) => {
  const body = getKycUploadResultBody(res);
  const next = {};
  ["panCard", "aadharCardFront", "aadharCardBack"].forEach((docType) => {
    const status = normalizeCkycDocStatus(body?.[docType]?.status);
    if (
      status === CKYC_DOC_STATUS.PENDING ||
      status === CKYC_DOC_STATUS.FAILED ||
      status === CKYC_DOC_STATUS.APPROVED
    ) {
      next[docType] = status;
    }
  });
  return next;
};

const CKYC_DOC_TO_VERIFICATION_KEY = {
  panCard: "panVerification",
  aadharCardFront: "aadharFrontVerification",
  aadharCardBack: "aadharBackVerification",
};

export const ckycVerificationFromUpload = (res = {}, uploadedType) => {
  const nested = readNestedCkycDocStatusFromUpload(res);
  const parsed = parseKycDocumentUpload(res, uploadedType);
  const next = {};

  Object.entries(nested).forEach(([docType, status]) => {
    const key = CKYC_DOC_TO_VERIFICATION_KEY[docType];
    if (key) {
      next[key] = status;
    }
  });

  const uploadedKey = CKYC_DOC_TO_VERIFICATION_KEY[uploadedType];
  if (uploadedKey && parsed.docStatus) {
    next[uploadedKey] = parsed.docStatus;
  }

  return next;
};

export const parseKycDocumentUpload = (res = {}, uploadedType) => {
  const code = String(res?.code || "").toUpperCase();
  const body = getKycUploadResultBody(res);
  const nested =
    uploadedType && body[uploadedType] && typeof body[uploadedType] === "object"
      ? body[uploadedType]
      : {};
  const message = res?.message || body.message || "";
  const normalizedStatus = normalizeCkycDocStatus(
    nested.status || body.documentStatus,
  );

  if (normalizedStatus === CKYC_DOC_STATUS.PENDING) {
    return pendingUpload(message);
  }

  if (normalizedStatus === CKYC_DOC_STATUS.FAILED) {
    return {
      ...FAILED_UPLOAD,
      message: message || "Failed to verify",
    };
  }

  if (normalizedStatus === CKYC_DOC_STATUS.APPROVED) {
    return approvedUpload(message);
  }

  if (code === "JCKYCP001" || code === "JCKYCU001") {
    return pendingUpload(message);
  }

  if (code === "JCKYCF001" || code.includes("FAIL")) {
    return {
      ...FAILED_UPLOAD,
      message: message || "Failed to verify",
    };
  }

  if (code === "JCKYCV001") {
    return approvedUpload(message);
  }

  return {
    ...FAILED_UPLOAD,
    message: message || "Failed to verify",
  };
};

export const getKycDocumentSuccessMessage = () =>
  "Document verified successfully";

export const kycUploadFlagsFromResponse = (res = {}, uploadedType) => {
  const parsed = parseKycDocumentUpload(res, uploadedType);
  const source = res?.result ?? res ?? {};

  const panUploaded = readUploadFlag(source, [
    "panCardUploaded",
    "isPanUploaded",
    "panUploaded",
  ]);
  const aadhaarFrontUploaded = readUploadFlag(source, [
    "aadharCardFrontUploaded",
    "aadhaarCardFrontUploaded",
    "isAadhaarFrontUploaded",
    "isAadharFrontUploaded",
    "aadharFrontUploaded",
    "aadhaarFrontUploaded",
  ]);
  const aadhaarBackUploaded = readUploadFlag(source, [
    "aadharCardBackUploaded",
    "aadhaarCardBackUploaded",
    "isAadhaarBackUploaded",
    "isAadharBackUploaded",
    "aadharBackUploaded",
    "aadhaarBackUploaded",
    "aadharCardbackUploaded",
  ]);

  return {
    isPanUploaded:
      uploadedType === "panCard"
        ? parsed.approved
          ? 1
          : 0
        : isKycDocumentUploaded(panUploaded)
          ? 1
          : 0,
    isAadhaarFrontUploaded:
      uploadedType === "aadharCardFront"
        ? parsed.approved
          ? 1
          : 0
        : isKycDocumentUploaded(aadhaarFrontUploaded)
          ? 1
          : 0,
    isAadhaarBackUploaded:
      uploadedType === "aadharCardBack"
        ? parsed.approved
          ? 1
          : 0
        : isKycDocumentUploaded(aadhaarBackUploaded)
          ? 1
          : 0,
  };
};

/** Preserve already-uploaded docs when API omits flags on partial updates. */
export const mergeStoredKycUploadFlag = (current, incoming) => {
  if (incoming === undefined || incoming === null) {
    return isKycDocumentUploaded(current) ? 1 : 0;
  }

  if (isKycDocumentUploaded(incoming)) {
    return 1;
  }

  return isKycDocumentUploaded(current) ? 1 : 0;
};

export const buildKycUploadUpdate = (existing = {}, uploadedType, res) => {
  const fromResponse = kycUploadFlagsFromResponse(res, uploadedType);
  const parsed = parseKycDocumentUpload(res, uploadedType);

  return {
    isPanUploaded:
      uploadedType === "panCard"
        ? parsed.approved || parsed.isPending
          ? 1
          : 0
        : mergeStoredKycUploadFlag(
            existing.isPanUploaded,
            fromResponse.isPanUploaded,
          ),
    isAadhaarFrontUploaded:
      uploadedType === "aadharCardFront"
        ? parsed.approved || parsed.isPending
          ? 1
          : 0
        : mergeStoredKycUploadFlag(
            existing.isAadhaarFrontUploaded,
            fromResponse.isAadhaarFrontUploaded,
          ),
    isAadhaarBackUploaded:
      uploadedType === "aadharCardBack"
        ? parsed.approved || parsed.isPending
          ? 1
          : 0
        : mergeStoredKycUploadFlag(
            existing.isAadhaarBackUploaded,
            fromResponse.isAadhaarBackUploaded,
          ),
    ...ckycVerificationFromUpload(res, uploadedType),
  };
};

export const buildKycUploadDisplayState = (
  existing = {},
  localUploads = {},
) => ({
  panCard:
    isKycDocumentUploaded(localUploads.panCard) ||
    isKycDocumentUploaded(existing.isPanUploaded),
  aadharCardFront:
    isKycDocumentUploaded(localUploads.aadharCardFront) ||
    isKycDocumentUploaded(existing.isAadhaarFrontUploaded),
  aadharCardBack:
    isKycDocumentUploaded(localUploads.aadharCardBack) ||
    isKycDocumentUploaded(existing.isAadhaarBackUploaded),
});

export const kycDocumentUploadSucceeded = (res, uploadedType) => {
  const parsed = parseKycDocumentUpload(res, uploadedType);
  return parsed.approved || parsed.isPending;
};

const KYC_PRODUCT_UPLOAD_SLICES = [
  (state) => state.onboarding,
  (state) => state.jarvisPrime?.primeOnboarding,
  (state) => state.jarvisRMS?.rmsOnboarding,
  (state) => state.jarvisOnestock?.onestockOnboarding,
  (state) => state.sentimentTracker?.sentimentTrackerOnboarding,
  (state) => state.PHC?.PHCOnboarding,
];

const NUMERIC_STAGE_MAP = {
  RISK_ASSESSMENT: 0,
  INVESTMENT_PREFERENCE: 1,
  GENERATE_PORTFOLIO: 2,
  CKYC: 3,
  DOCUMENT_SIGNING: 4,
  BROKER_TRANSACTION: 5,
};

const CKYC_COMPLETE_STRING_STAGES = new Set([
  "DOCUMENT_SIGNING",
  "BROKER_TRANSACTION",
  "CHOOSE_BROKER",
  "LINK_BROKER",
]);

const KYC_PRODUCT_CONFIG = [
  {
    code: "PORTFOLIO",
    getSlice: (state) => state.onboarding,
    loginStageKey: "onboardingData",
    profileKey: "PORTFOLIO",
    isNumericStage: true,
    getStage: (slice) => slice?.currentStage,
  },
  {
    code: "PRIME",
    getSlice: (state) => state.jarvisPrime?.primeOnboarding,
    loginStageKey: "primeOnboardingData",
    profileKey: "PRIME",
    isNumericStage: true,
    getStage: (slice) => slice?.currentStagePrime,
  },
  {
    code: "RMS",
    getSlice: (state) => state.jarvisRMS?.rmsOnboarding,
    loginStageKey: "rmsOnboardingData",
    profileKey: "RMS",
    isNumericStage: false,
    getStage: (slice) => slice?.currentStage,
  },
  {
    code: "ONESTOCK",
    getSlice: (state) => state.jarvisOnestock?.onestockOnboarding,
    loginStageKey: "onestockOnboardingData",
    profileKey: "ONESTOCK",
    isNumericStage: false,
    getStage: (slice) => slice?.currentStage,
  },
  {
    code: "SENTIMENT",
    getSlice: (state) => state.sentimentTracker?.sentimentTrackerOnboarding,
    loginStageKey: "sentimentTrackerOnboardingData",
    profileKey: "SENTIMENT",
    isNumericStage: false,
    getStage: (slice) => slice?.currentStage,
  },
  {
    code: "PHC",
    getSlice: (state) => state.PHC?.PHCOnboarding,
    loginStageKey: "phcOnboardingData",
    profileKey: "PHC",
    isNumericStage: false,
    getStage: (slice) => slice?.currentStage,
    getCkycDone: (slice) => slice?.CKYCDone,
  },
];

const isTruthyKycCompleted = (value) =>
  value === true || value === 1 || value === "1";

const isStringStageCkycCompleted = (stage) =>
  Boolean(stage) &&
  stage !== "CKYC" &&
  CKYC_COMPLETE_STRING_STAGES.has(stage);

const resolveNumericStage = (stage) => {
  if (stage === null || stage === undefined || stage === "") {
    return null;
  }
  if (typeof stage === "number" || !Number.isNaN(Number(stage))) {
    return Number(stage);
  }
  return NUMERIC_STAGE_MAP[stage] ?? null;
};

const isNumericStageCkycCompleted = (stage) => {
  const resolvedStage = resolveNumericStage(stage);
  return resolvedStage !== null && resolvedStage >= 4;
};

const mergeUploadFlags = (target, source) => ({
  isPanUploaded:
    target.isPanUploaded || isKycDocumentUploaded(source.isPanUploaded) ? 1 : 0,
  isAadhaarFrontUploaded:
    target.isAadhaarFrontUploaded ||
    isKycDocumentUploaded(source.isAadhaarFrontUploaded)
      ? 1
      : 0,
  isAadhaarBackUploaded:
    target.isAadhaarBackUploaded ||
    isKycDocumentUploaded(source.isAadhaarBackUploaded)
      ? 1
      : 0,
});

const uploadFlagsFromSlice = (slice = {}, isProductComplete = false) => {
  if (isProductComplete) {
    return {
      isPanUploaded: 1,
      isAadhaarFrontUploaded: 1,
      isAadhaarBackUploaded: 1,
    };
  }

  return {
    isPanUploaded: isKycDocumentUploaded(slice.isPanUploaded) ? 1 : 0,
    isAadhaarFrontUploaded: isKycDocumentUploaded(slice.isAadhaarFrontUploaded)
      ? 1
      : 0,
    isAadhaarBackUploaded: isKycDocumentUploaded(slice.isAadhaarBackUploaded)
      ? 1
      : 0,
  };
};

const isConfiguredProductCkycCompleted = (
  state = {},
  product,
  { loginProfile, customerProfile } = {},
) => {
  if (isTruthyKycCompleted(customerProfile?.[product.profileKey]?.isKYCCompleted)) {
    return true;
  }

  const slice = product.getSlice(state) || {};
  const reduxStage = product.getStage(slice);

  if (product.isNumericStage && isNumericStageCkycCompleted(reduxStage)) {
    return true;
  }

  if (!product.isNumericStage) {
    if (Number(product.getCkycDone?.(slice)) === 1) {
      return true;
    }
    if (isStringStageCkycCompleted(reduxStage)) {
      return true;
    }
  }

  const loginStage = loginProfile?.[product.loginStageKey]?.currentStage;
  if (product.isNumericStage && isNumericStageCkycCompleted(loginStage)) {
    return true;
  }
  if (!product.isNumericStage && isStringStageCkycCompleted(loginStage)) {
    return true;
  }

  return false;
};

const collectUploadFlags = (state = {}, { excludeCode = null } = {}) => {
  const loginProfile = state.customer?.loginProfile || {};
  const customerProfile = state.customer?.profile || {};
  let flags = {
    isPanUploaded: 0,
    isAadhaarFrontUploaded: 0,
    isAadhaarBackUploaded: 0,
  };

  KYC_PRODUCT_CONFIG.forEach((product) => {
    if (excludeCode && product.code === excludeCode) {
      return;
    }

    const slice = product.getSlice(state) || {};
    const isProductComplete = isConfiguredProductCkycCompleted(state, product, {
      loginProfile,
      customerProfile,
    });
    flags = mergeUploadFlags(
      flags,
      uploadFlagsFromSlice(slice, isProductComplete),
    );
  });

  if (!excludeCode) {
    if (isKycDocumentUploaded(loginProfile.isPanUploaded)) {
      flags.isPanUploaded = 1;
    }
    if (isKycDocumentUploaded(loginProfile.isAadhaarFrontUploaded)) {
      flags.isAadhaarFrontUploaded = 1;
    }
    if (isKycDocumentUploaded(loginProfile.isAadhaarBackUploaded)) {
      flags.isAadhaarBackUploaded = 1;
    }
  }

  return flags;
};

export const isCkycCompletedInAnyProduct = (state = {}) => {
  if (isTruthyKycCompleted(state.common?.isCkycDone)) {
    return true;
  }

  const loginProfile = state.customer?.loginProfile || {};
  const customerProfile = state.customer?.profile || {};

  if (isTruthyKycCompleted(loginProfile.isKYCCompleted)) {
    return true;
  }

  if (isTruthyKycCompleted(customerProfile.isKYCCompleted)) {
    return true;
  }

  if (
    KYC_PRODUCT_CONFIG.some((product) =>
      isConfiguredProductCkycCompleted(state, product, {
        loginProfile,
        customerProfile,
      }),
    )
  ) {
    return true;
  }

  const crossProductFlags = getCrossProductKycUploadFlags(state);
  return (
    isKycDocumentUploaded(crossProductFlags.isPanUploaded) &&
    isKycDocumentUploaded(crossProductFlags.isAadhaarFrontUploaded) &&
    isKycDocumentUploaded(crossProductFlags.isAadhaarBackUploaded)
  );
};

export const isCkycCompletedInOtherProducts = (state = {}, currentProductCode) => {
  const loginProfile = state.customer?.loginProfile || {};
  const customerProfile = state.customer?.profile || {};

  const completedInAnotherProduct = KYC_PRODUCT_CONFIG.some(
    (product) =>
      product.code !== currentProductCode &&
      isConfiguredProductCkycCompleted(state, product, {
        loginProfile,
        customerProfile,
      }),
  );

  if (completedInAnotherProduct) {
    return true;
  }

  const otherProductFlags = getOtherProductsKycUploadFlags(
    state,
    currentProductCode,
  );

  return (
    isKycDocumentUploaded(otherProductFlags.isPanUploaded) &&
    isKycDocumentUploaded(otherProductFlags.isAadhaarFrontUploaded) &&
    isKycDocumentUploaded(otherProductFlags.isAadhaarBackUploaded)
  );
};

export const getCrossProductKycUploadFlags = (state = {}) =>
  collectUploadFlags(state);

export const getOtherProductsKycUploadFlags = (state = {}, currentProductCode) =>
  collectUploadFlags(state, { excludeCode: currentProductCode });

export const mergeKycUploadFlags = (current = {}, crossProduct = {}) => ({
  isPanUploaded:
    isKycDocumentUploaded(current.isPanUploaded) ||
    isKycDocumentUploaded(crossProduct.isPanUploaded)
      ? 1
      : 0,
  isAadhaarFrontUploaded:
    isKycDocumentUploaded(current.isAadhaarFrontUploaded) ||
    isKycDocumentUploaded(crossProduct.isAadhaarFrontUploaded)
      ? 1
      : 0,
  isAadhaarBackUploaded:
    isKycDocumentUploaded(current.isAadhaarBackUploaded) ||
    isKycDocumentUploaded(crossProduct.isAadhaarBackUploaded)
      ? 1
      : 0,
});

export const buildKycUploadDisableState = (
  crossProductFlags = {},
  { ckycCompletedAnywhere = false } = {},
) => ({
  panCard:
    ckycCompletedAnywhere ||
    isKycDocumentUploaded(crossProductFlags.isPanUploaded),
  aadharCardFront:
    ckycCompletedAnywhere ||
    isKycDocumentUploaded(crossProductFlags.isAadhaarFrontUploaded),
  aadharCardBack:
    ckycCompletedAnywhere ||
    isKycDocumentUploaded(crossProductFlags.isAadhaarBackUploaded),
});

export const getKycCrossProductProps = (
  state = {},
  currentProductCode = null,
) => {
  const crossProductKycUploadFlags = getCrossProductKycUploadFlags(state);
  const otherProductsUploadFlags = currentProductCode
    ? getOtherProductsKycUploadFlags(state, currentProductCode)
    : crossProductKycUploadFlags;
  const ckycCompletedAnywhere = isCkycCompletedInAnyProduct(state);
  const ckycCompletedElsewhere = currentProductCode
    ? isCkycCompletedInOtherProducts(state, currentProductCode)
    : ckycCompletedAnywhere;

  return {
    crossProductKycUploadFlags,
    ckycCompletedAnywhere,
    ckycCompletedElsewhere,
    disableUpload: buildKycUploadDisableState(otherProductsUploadFlags, {
      ckycCompletedAnywhere: ckycCompletedElsewhere,
    }),
  };
};

export const buildMergedKycUploadDisplayState = (
  currentFlags = {},
  crossProductFlags = {},
  localUploads = {},
) =>
  buildKycUploadDisplayState(
    mergeKycUploadFlags(currentFlags, crossProductFlags),
    localUploads,
  );
